import {
  AudioOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  FileOutlined,
  InfoCircleOutlined,
  PaperClipOutlined,
} from '@ant-design/icons'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { useAddAlert } from '@/hooks/useAddAlert'
import cn from 'classnames'
import { ChangeEvent, PropsWithChildren, useId, useMemo } from 'react'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import { isCrmIntegrationOutgoingAttachmentsEnabled } from '../../lib/crmChatProviderKey'
import {
  CRM_INTEGRATION_OUTGOING_FILE_ACCEPT,
  isCrmIntegrationOutgoingAttachableFile,
  validateCrmIntegrationOutgoingAttachmentSize,
  validateCrmIntegrationOutgoingVideoFile,
} from '../../lib/crmIntegrationOutgoingAttachment'
import {
  getWazzupOutgoingAttachmentsHint,
  getWazzupOutgoingFileAccept,
  isWazzupOutgoingVoiceRecorderAllowed,
  validateWazzupOutgoingAttachment,
} from '../../lib/crmIntegrationWazzupOutgoingAttachment'
import classes from '../../styles/crmIntegrationComposer.module.css'

const ATTACHMENTS_HINT = (
  <>
    Можно отправить изображение (JPG, PNG, GIF, WEBP), документ (PDF, DOC, XLS,
    ZIP и др.) или видео (MP4, MOV, WEBM).
    <br />
    Текст сообщения необязателен, если прикреплён файл.
  </>
)

const UNSUPPORTED_FILE_ALERT =
  'Можно прикрепить изображение, документ или видео в поддерживаемом формате.'

export type CrmIntegrationTextChatComposerProps = PropsWithChildren<{
  compact?: boolean
  disabled?: boolean
  provider: CrmChatProviderKey
  /** Wazzup: external_chat_type текущего диалога. */
  wazzupChannelType?: string | null
  attachmentFile?: File | null
  onAttachmentFileChange?: (file: File | null) => void
  voiceMode?: boolean
  onOpenVoiceMode?: () => void
  onCloseVoiceMode?: () => void
}>

const CrmIntegrationTextChatComposer = ({
  compact = false,
  disabled = false,
  provider,
  wazzupChannelType = null,
  attachmentFile = null,
  onAttachmentFileChange,
  voiceMode = false,
  onOpenVoiceMode,
  onCloseVoiceMode,
  children,
}: CrmIntegrationTextChatComposerProps) => {
  const addAlert = useAddAlert()
  const fileInputId = useId()
  const isAttachDisabled = disabled || Boolean(attachmentFile)
  const outgoingAttachmentsEnabled =
    isCrmIntegrationOutgoingAttachmentsEnabled(provider)
  const voiceRecorderAllowed =
    provider !== 'wazzup' ||
    isWazzupOutgoingVoiceRecorderAllowed(wazzupChannelType)

  const fileAccept = useMemo(() => {
    if (provider === 'wazzup') {
      return getWazzupOutgoingFileAccept(wazzupChannelType)
    }
    return CRM_INTEGRATION_OUTGOING_FILE_ACCEPT
  }, [provider, wazzupChannelType])

  const attachmentsHint = useMemo(() => {
    if (provider === 'wazzup') {
      return getWazzupOutgoingAttachmentsHint(wazzupChannelType)
    }
    return ATTACHMENTS_HINT
  }, [provider, wazzupChannelType])

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    if (!outgoingAttachmentsEnabled) {
      return
    }
    const file = event.target.files?.[0]
    event.target.value = ''

    if (!file) {
      return
    }

    if (provider === 'wazzup') {
      const wazzupError = validateWazzupOutgoingAttachment(
        file,
        wazzupChannelType,
      )
      if (wazzupError) {
        addAlert({
          type: 'error',
          title: 'Нельзя отправить файл',
          text: wazzupError,
        })
        return
      }
      onAttachmentFileChange?.(file)
      return
    }

    if (!isCrmIntegrationOutgoingAttachableFile(file)) {
      addAlert({
        type: 'error',
        title: 'Неподдерживаемый файл',
        text: UNSUPPORTED_FILE_ALERT,
      })
      return
    }

    const sizeValidationError = validateCrmIntegrationOutgoingAttachmentSize(
      file,
      provider,
    )
    if (sizeValidationError) {
      addAlert({
        type: 'error',
        title: 'Файл слишком большой',
        text: sizeValidationError,
      })
      return
    }

    const videoValidationError = validateCrmIntegrationOutgoingVideoFile(
      file,
      provider,
    )
    if (videoValidationError) {
      addAlert({
        type: 'error',
        title: 'Неподдерживаемое видео',
        text: videoValidationError,
      })
      return
    }

    onAttachmentFileChange?.(file)
  }

  return (
    <footer
      className={cn(classes.composer, {
        [classes.composerCompact]: compact,
      })}
    >
      {outgoingAttachmentsEnabled && attachmentFile ? (
        <div className={classes.composerAttachments}>
          <ul className={classes.composerAttachmentsList}>
            <li className={classes.composerAttachmentItem}>
              <div className={classes.composerAttachmentCard}>
                <button
                  type="button"
                  className={classes.composerAttachmentRemove}
                  aria-label={`Убрать файл ${attachmentFile.name}`}
                  disabled={disabled}
                  onClick={() => onAttachmentFileChange?.(null)}
                >
                  <CloseCircleOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </button>
                <FileOutlined
                  className={classes.composerAttachmentIcon}
                  aria-hidden
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                <span
                  className={classes.composerAttachmentName}
                  title={attachmentFile.name}
                >
                  {attachmentFile.name}
                </span>
              </div>
            </li>
          </ul>
        </div>
      ) : null}

      <div className={classes.composerMainRow}>
        {voiceMode && outgoingAttachmentsEnabled ? (
          <>
            <button
              type="button"
              className={classes.attachButton}
              aria-label="Отменить запись голосового сообщения"
              onClick={() => onCloseVoiceMode?.()}
            >
              <CloseOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </button>
            {children}
          </>
        ) : (
          <>
            {outgoingAttachmentsEnabled ? (
              <div className={classes.composerActions}>
                <input
                  id={fileInputId}
                  type="file"
                  accept={fileAccept || undefined}
                  className={classes.composerFileInput}
                  disabled={isAttachDisabled || !fileAccept}
                  tabIndex={-1}
                  onChange={handleFilesSelected}
                />
                <label
                  htmlFor={fileInputId}
                  className={cn(classes.attachButton, {
                    [classes.attachButtonDisabled]:
                      isAttachDisabled || !fileAccept,
                  })}
                  aria-label="Прикрепить файл"
                  aria-disabled={isAttachDisabled || !fileAccept}
                >
                  <PaperClipOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </label>
                <TooltipWrapper
                  title={attachmentsHint}
                  placement="topLeft"
                  overlayClassName={classes.formatsTooltipOverlay}
                >
                  <span className={classes.infoTooltipTrigger}>
                    <button
                      type="button"
                      className={cn(classes.infoButton, {
                        [classes.infoButtonDisabled]: disabled,
                      })}
                      aria-label="Какие файлы можно отправить"
                      aria-disabled={disabled}
                      tabIndex={disabled ? -1 : 0}
                    >
                      <InfoCircleOutlined
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                    </button>
                  </span>
                </TooltipWrapper>
                {voiceRecorderAllowed ? (
                  <button
                    type="button"
                    className={cn(classes.attachButton, {
                      [classes.attachButtonDisabled]: disabled,
                    })}
                    aria-label="Голосовое сообщение"
                    aria-disabled={disabled}
                    disabled={disabled}
                    tabIndex={disabled ? -1 : 0}
                    onClick={() => onOpenVoiceMode?.()}
                  >
                    <AudioOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  </button>
                ) : null}
              </div>
            ) : null}
            {children}
          </>
        )}
      </div>
    </footer>
  )
}

export default CrmIntegrationTextChatComposer
